import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../design/brainTheme";
import {
  getAaronVersion,
  getAaronVersionSynthesis,
  patchAaronVersion,
  type ReleaseEpicGroup,
  type ReleaseMilestone,
  type ReleaseSynthesis,
  type VersionDetail,
} from "../lib/brain-api";
import { ProgressRing } from "./ProgressRing";
import { Segmented } from "./Segmented";
import { Icon } from "../design/ui";
import {
  AssigneeMonogram,
  PriorityPill,
  RefCell,
  RegisterTable,
  StatusDot,
  type RegisterTableColumn,
} from "./RegisterTable";
import { jiraStatusTone, normalisePriority } from "./registerRows";
import { JIRA_BROWSE } from "./actionRegisterStatus";

const c = brainTokens.color;

// The four segments, in the order the handoff draws them. Mutually exclusive and
// summing to `total`, so the bar is a true part-to-whole — unlike done/inProgress
// /toDo + blocked, which overlap (a blocked issue is also in-progress or to-do).
const SEGMENTS = [
  { key: "done", label: "done", color: c.successDot },
  { key: "inProgress", label: "in progress", color: c.warnDot },
  { key: "blocked", label: "blocked", color: c.dangerText },
  { key: "notStarted", label: "not started", color: c.border },
] as const;

type SegmentCounts = { done: number; inProgress: number; blocked: number; notStarted: number };

/**
 * Four-segment progress bar + dot legend (handoff §5.1). Counts are ISSUES, not
 * story points — confirmed with Isaac; Aeronet does not point.
 */
export function ReleaseSegmentBar({ segments }: { segments: SegmentCounts }) {
  const total = SEGMENTS.reduce((n, s) => n + (segments[s.key] || 0), 0);
  if (!total) return null;
  return (
    <Box data-testid="release-segment-bar">
      <Box
        role="img"
        aria-label={SEGMENTS.filter((s) => segments[s.key] > 0)
          .map((s) => `${segments[s.key]} ${s.label}`)
          .join(", ")}
        sx={{
          display: "flex",
          height: 10,
          borderRadius: `${brainTokens.radius.pill}px`,
          overflow: "hidden",
          background: c.borderLight,
        }}
      >
        {SEGMENTS.map((s) => {
          const n = segments[s.key] || 0;
          if (!n) return null;
          return (
            <Box
              key={s.key}
              title={`${n} ${s.label}`}
              sx={{ width: `${(n / total) * 100}%`, background: s.color }}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.1 }}>
        {SEGMENTS.map((s) => (
          <Box
            key={s.key}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}
          >
            <Box
              aria-hidden
              sx={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: s.color }}
            />
            <Box component="span" sx={{ fontSize: 12.5, color: c.muted }}>
              <Box component="b" sx={{ fontFamily: brainTokens.font.mono, fontWeight: 700, color: c.ink2 }}>
                {segments[s.key] || 0}
              </Box>{" "}
              {s.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Which milestone is the NEXT one — the first that has not passed. */
export function nextMilestoneIndex(milestones: ReleaseMilestone[], today: string): number {
  return milestones.findIndex((m) => m.date >= today);
}

/** Milestones: dot + label + date, struck through when passed, amber when next. */
export function MilestoneColumn({
  milestones,
  today,
}: {
  milestones: ReleaseMilestone[];
  today: string;
}) {
  const next = nextMilestoneIndex(milestones, today);
  return (
    <Box data-testid="release-milestones" sx={{ minWidth: 168 }}>
      <Box
        component="span"
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.11em",
          color: c.faint,
        }}
      >
        MILESTONES
      </Box>
      {milestones.length === 0 ? (
        // No invented code-freeze date. Jira holds no dates for this version and
        // the card says exactly that.
        <Box sx={{ fontSize: 12.5, color: c.muted2, mt: 0.9, lineHeight: 1.5 }}>
          No dates set on the Jira version.
        </Box>
      ) : (
        milestones.map((m, i) => {
          const passed = i < next || next === -1;
          const isNext = i === next;
          return (
            <Box
              key={m.key}
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.9, whiteSpace: "nowrap" }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  flex: "none",
                  background: isNext ? c.warnDot : passed ? c.successDot : c.border,
                }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  fontWeight: isNext ? 650 : 500,
                  color: isNext ? c.warnText : passed ? c.muted2 : c.ink3,
                  textDecoration: passed ? "line-through" : "none",
                }}
              >
                {m.label}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 12,
                  color: isNext ? c.warnText : c.muted2,
                  textDecoration: passed ? "line-through" : "none",
                }}
              >
                {m.date.slice(5)}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
}

export type EpicFilter = "all" | "open" | "blocked";

/** `All epics / Open only / Blocked`, applied to the epic list. */
export function filterEpics(epics: ReleaseEpicGroup[], filter: EpicFilter): ReleaseEpicGroup[] {
  if (filter === "open") return epics.filter((e) => e.done < e.total);
  if (filter === "blocked") return epics.filter((e) => e.blocked > 0);
  return epics;
}

const EPIC_ISSUE_COLUMNS: RegisterTableColumn[] = [
  { key: "priority", label: "Pri", width: "48px" },
  { key: "ref", label: "Ref", width: "108px" },
  { key: "summary", label: "Summary", width: "minmax(0,1fr)" },
  { key: "status", label: "Status", width: "158px", collapse: true },
  { key: "assignee", label: "", width: "26px", align: "center" },
];

/** One epic row: mono key, name, module, blocked chip, done/total, mini bar. */
function EpicRow({
  epic,
  open,
  onToggle,
}: {
  epic: ReleaseEpicGroup;
  open: boolean;
  onToggle: () => void;
}) {
  const pct = epic.total ? Math.round((epic.done / epic.total) * 100) : 0;
  return (
    <Box sx={{ borderBottom: `1px solid ${c.borderLight3}`, "&:last-of-type": { borderBottom: "none" } }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "92px minmax(0,1fr) auto", md: "92px minmax(0,1fr) auto 76px 120px" },
          alignItems: "center",
          gap: 1.75,
          width: "100%",
          p: "11px 14px",
          border: "none",
          background: open ? c.panelBg2 : "none",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          "&:hover": { background: c.panelBg2 },
        }}
      >
        <Box
          component="span"
          sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: c.ink4 }}
        >
          {epic.epicKey}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 13.5,
              fontWeight: 500,
              color: c.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {epic.epicName}
          </Box>
          {epic.module && epic.module !== epic.epicName && (
            <Box
              sx={{
                fontSize: 12,
                color: c.muted2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mt: "1px",
              }}
            >
              {epic.module}
            </Box>
          )}
        </Box>
        <Box sx={{ justifySelf: "start" }}>
          {epic.blocked > 0 && (
            <Box
              component="span"
              title={`${epic.blocked} blocked`}
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                px: "8px",
                py: "2px",
                borderRadius: `${brainTokens.radius.pill}px`,
                background: c.dangerBg,
                color: c.dangerText,
                whiteSpace: "nowrap",
              }}
            >
              {epic.blocked} blocked
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            display: { xs: "none", md: "block" },
            fontFamily: brainTokens.font.mono,
            fontSize: 12,
            color: c.muted,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {epic.done}/{epic.total}
        </Box>
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            height: 6,
            borderRadius: `${brainTokens.radius.pill}px`,
            background: c.borderLight,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: `${pct}%`, height: "100%", background: c.teal }} />
        </Box>
      </Box>

      {open && (
        <Box sx={{ background: c.white, borderTop: `1px solid ${c.borderLight3}`, pt: 1 }}>
          <RegisterTable
            columns={EPIC_ISSUE_COLUMNS}
            empty="This epic has no issues in the release."
            dense
            rows={epic.issues.map((i) => ({
              id: i.key,
              spine: i.blocked
                ? ("danger" as const)
                : String(i.statusCategory).toLowerCase() === "done"
                  ? ("muted" as const)
                  : ("teal" as const),
              faded: String(i.statusCategory).toLowerCase() === "done",
              title: `${i.key} — ${i.summary}`,
              cells: {
                priority: <PriorityPill priority={normalisePriority(i.priority)} />,
                ref: <RefCell label={i.key} href={`${JIRA_BROWSE}/${encodeURIComponent(i.key)}`} />,
                summary: (
                  <Box
                    title={i.summary}
                    sx={{
                      fontSize: 13.5,
                      color: c.ink,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {i.summary}
                  </Box>
                ),
                status: (
                  <StatusDot
                    label={i.status || "—"}
                    tone={jiraStatusTone(i.statusCategory, i.status, i.blocked)}
                  />
                ),
                assignee: <AssigneeMonogram name={i.assignee} />,
              },
            }))}
          />
        </Box>
      )}
    </Box>
  );
}

export interface ReleaseHeroEpic {
  name: string;
  done: number;
  total: number;
}

export interface ReleaseMasterCardProps {
  /** Optional override — otherwise loaded from /admin/aaron-version/synthesis. */
  heroes?: ReleaseHeroEpic[];
  /** Optional override — otherwise from synthesis KPIs. */
  unlinkedTickets?: number | null;
  /** Optional override — otherwise project deadlineDate when present. */
  trainDate?: string | null;
  canEdit?: boolean;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        minWidth: 88,
        px: 1.25,
        py: 1,
        borderRadius: `${brainTokens.radius.card}px`,
        border: `1px solid ${c.border}`,
        background: c.canvas,
      }}
    >
      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 18, fontWeight: 700, color: c.ink, lineHeight: 1.2 }}>
        {value}
      </Box>
      <Box
        sx={{
          fontSize: 10.5,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: c.muted,
          mt: 0.35,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

function heroDoneFromMix(statusMix: Record<string, number>): number {
  return Object.entries(statusMix).reduce((n, [name, count]) => {
    const k = name.toLowerCase();
    if (k.includes("done") || k.includes("closed") || k.includes("resolved") || k === "complete") return n + count;
    return n;
  }, 0);
}

function heroesFromSynthesis(s: ReleaseSynthesis): ReleaseHeroEpic[] {
  return s.heroes.map((h) => ({
    name: h.epicName,
    done: heroDoneFromMix(h.statusMix),
    total: h.issues,
  }));
}

function VersionEditor({
  initial,
  onCancel,
  onSaved,
}: {
  initial: { displayVersion: string; jiraFixVersion: string };
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [displayVersion, setDisplay] = useState(initial.displayVersion);
  const [jiraFixVersion, setFix] = useState(initial.jiraFixVersion);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!displayVersion.trim() || !jiraFixVersion.trim()) return;
    setSaving(true);
    try {
      await patchAaronVersion({
        displayVersion: displayVersion.trim(),
        jiraFixVersion: jiraFixVersion.trim(),
      });
      await onSaved();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed.", { variant: "error" });
      setSaving(false);
    }
  };
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        background: c.pageBg,
        border: `1px solid ${c.border}`,
        borderLeft: `4px solid ${c.teal}`,
        borderRadius: `${brainTokens.radius.card}px`,
      }}
    >
      <Box sx={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: c.tealInk, mb: 1.5 }}>
        Current version
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Box component="label" sx={{ fontSize: 12, color: c.muted }}>
          Display version
          <Box
            component="input"
            value={displayVersion}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplay(e.target.value)}
            placeholder="4.16.0"
            sx={{
              display: "block",
              mt: 0.5,
              width: 160,
              px: 1,
              py: 0.75,
              border: `1px solid ${c.border}`,
              borderRadius: `${brainTokens.radius.button}px`,
              fontFamily: brainTokens.font.sans,
              fontSize: 13,
            }}
          />
        </Box>
        <Box component="label" sx={{ fontSize: 12, color: c.muted }}>
          Jira fixVersion
          <Box
            component="input"
            value={jiraFixVersion}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFix(e.target.value)}
            placeholder="4.16"
            sx={{
              display: "block",
              mt: 0.5,
              width: 160,
              px: 1,
              py: 0.75,
              border: `1px solid ${c.border}`,
              borderRadius: `${brainTokens.radius.button}px`,
              fontFamily: brainTokens.font.sans,
              fontSize: 13,
            }}
          />
        </Box>
        <Button variant="contained" size="small" disabled={saving} onClick={() => void save()}>
          Save
        </Button>
        {(initial.displayVersion || initial.jiraFixVersion) && (
          <Button size="small" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Master release card for the standup Projects tab.
 * Base numbers from getAaronVersion(); heroes / linked / unlinked from synthesis when available.
 */
export function ReleaseMasterCard({
  heroes: heroesProp,
  unlinkedTickets: unlinkedProp,
  trainDate: trainDateProp,
  canEdit = false,
}: ReleaseMasterCardProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [v, setV] = useState<VersionDetail | null>(null);
  const [synthesis, setSynthesis] = useState<ReleaseSynthesis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [epicFilter, setEpicFilter] = useState<EpicFilter>("all");
  const [openEpic, setOpenEpic] = useState<string | null>(null);
  const [synthesisError, setSynthesisError] = useState(false);

  const load = useCallback(async () => {
    try {
      const version = await getAaronVersion();
      setV(version);
      setError(false);
      // Only hit synthesis after a real fixVersion is pinned — never on an empty default.
      const fix =
        version.release?.fixVersion ||
        (typeof version.project?.meta?.jiraFixVersion === "string" ? version.project.meta.jiraFixVersion : "");
      if (fix && version.project && version.release) {
        try {
          const syn = await getAaronVersionSynthesis();
          setSynthesis(syn.synthesis);
          setSynthesisError(!syn.synthesis);
        } catch {
          setSynthesis(null);
          setSynthesisError(true);
        }
      } else {
        setSynthesis(null);
        setSynthesisError(false);
      }
    } catch {
      setError(true);
      setV(null);
      setSynthesis(null);
      setSynthesisError(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Every epic in the release with all of its issues. Absent from the deployed
  // API until this ships, so the card degrades to its non-expanding form.
  const epics = useMemo<ReleaseEpicGroup[]>(() => synthesis?.epicGroups ?? [], [synthesis]);
  const shownEpics = useMemo(() => filterEpics(epics, epicFilter), [epics, epicFilter]);
  const today = useMemo(
    () => new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" }),
    [],
  );

  if (loading) return null;

  // Manual edit path — type the version, save, then we fetch against what you typed.
  if (editing || (error && canEdit) || (!(v && v.project && v.release) && canEdit)) {
    const initial = v?.release
      ? { displayVersion: v.release.displayVersion, jiraFixVersion: v.release.fixVersion }
      : { displayVersion: "", jiraFixVersion: "" };
    return (
      <VersionEditor
        initial={initial}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          setError(false);
          setLoading(true);
          await load();
          enqueueSnackbar("Version saved — fetching release status…", { variant: "success" });
        }}
      />
    );
  }

  if (error || !(v && v.project && v.release)) {
    return (
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          background: c.pageBg,
          border: `1px solid ${c.border}`,
          borderLeft: `4px solid ${c.teal}`,
          borderRadius: `${brainTokens.radius.card}px`,
        }}
      >
        <Box>
          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: c.tealInk }}>
            Current version
          </Box>
          <Box sx={{ fontSize: 15, fontWeight: 600, color: c.ink, mt: 0.25 }}>
            {error ? "Couldn\u2019t load version status" : "No current version pinned"}
          </Box>
        </Box>
        <Button size="small" onClick={() => void load()}>
          Retry
        </Button>
      </Box>
    );
  }

  const rel = v!.release!;
  const ver = rel.displayVersion;
  const dash = "—";

  const synHeroes = synthesis?.configured ? heroesFromSynthesis(synthesis) : [];
  const heroes = heroesProp ?? synHeroes;
  const unlinked =
    unlinkedProp != null
      ? unlinkedProp
      : synthesis?.configured
        ? synthesis.kpis.ticketsUnlinked
        : null;
  const trainDate =
    trainDateProp ??
    (typeof v!.project?.deadlineDate === "string" ? v!.project.deadlineDate : null);

  const kpiDev = rel.configured ? String(rel.total) : dash;
  const kpiDone = rel.configured ? `${rel.percent}%` : dash;
  const kpiLinked =
    synthesis?.configured
      ? `${synthesis.kpis.ticketsLinked}/${synthesis.kpis.tickets}`
      : dash;
  const kpiHeroes = heroes.length ? String(heroes.length) : dash;
  const kpiTrain = trainDate?.trim() || dash;

  // Title click ALWAYS expands in place (heroes + epics). Never navigate to a
  // guessed `aeronet-{fixVersion}` id — that 404s when the per-release project
  // was never created and the pointer still lives on `aeronet-app`.
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2.25,
        background: c.pageBg,
        border: `1px solid ${c.border}`,
        borderLeft: `4px solid ${c.teal}`,
        borderRadius: `${brainTokens.radius.card}px`,
        boxShadow: brainTokens.shadow.card,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        {rel.configured ? (
          <ProgressRing pct={rel.percent} size={66} />
        ) : (
          <Box sx={{ color: c.teal, display: "flex" }}>
            <Icon name="rocket" size={40} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: c.tealInk,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ color: c.tealInk, display: "flex" }}>
              <Icon name="rocket" size={13} />
            </Box>
            AERONET {ver} — RELEASE
            <Box component="span" sx={{ color: c.muted }}>
              ·
            </Box>
            <Box component="span" sx={{ color: c.ink2, fontWeight: 650 }}>
              master project
            </Box>
          </Box>
          <Tooltip title={expanded ? "Collapse release map" : "Expand heroes and epics"}>
            <Box
              component="button"
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((o) => !o)}
              sx={{
                display: "block",
                mt: 0.5,
                p: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: c.ink,
                fontFamily: brainTokens.font.sans,
                "&:hover": { color: c.tealInk },
              }}
            >
              Aeronet {ver}
              <Box
                component="span"
                aria-hidden
                sx={{
                  fontSize: 14,
                  color: c.teal,
                  ml: 1,
                  display: "inline-block",
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                ⌄
              </Box>
            </Box>
          </Tooltip>
          <Box sx={{ fontSize: 13, color: c.muted, mt: 0.35 }}>
            master project — everything shipping in this train
          </Box>
        </Box>

        {rel.milestones !== undefined && <MilestoneColumn milestones={rel.milestones} today={today} />}
        {canEdit && (
          <Button size="small" onClick={() => setEditing(true)} sx={{ alignSelf: "flex-start" }}>
            Edit
          </Button>
        )}
      </Box>

      {/* Four-segment bar + legend. Counts are ISSUES, not points. */}
      {rel.segments && <ReleaseSegmentBar segments={rel.segments} />}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Kpi label="Dev issues" value={kpiDev} />
        <Kpi label="% done" value={kpiDone} />
        <Kpi label="Tickets linked" value={kpiLinked} />
        <Kpi label="Heroes" value={kpiHeroes} />
        <Kpi label="Train date" value={kpiTrain} />
      </Box>

      {expanded && (
        <Box
          data-testid="release-epics"
          sx={{
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.card}px`,
            overflow: "hidden",
            background: c.white,
          }}
        >
          {synthesisError && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                p: "14px 16px",
                borderBottom: `1px solid ${c.borderLight3}`,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ fontSize: 13.5, color: c.muted }}>
                Couldn&apos;t load the release map (heroes + epics). Version counts above are fine — this is a separate Jira pull.
              </Box>
              <Button
                size="small"
                onClick={() => {
                  setLoading(true);
                  void load();
                }}
              >
                Retry
              </Button>
            </Box>
          )}

          {!synthesisError && (
            <Box sx={{ p: "14px 16px", borderBottom: `1px solid ${c.borderLight3}` }}>
              <Box
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.11em",
                  color: c.faint,
                  mb: 1,
                }}
              >
                HEROES
              </Box>
              {heroes.length === 0 ? (
                <Box sx={{ fontSize: 13, color: c.muted }}>
                  No hero epics yet (needs ≥8 issues tagged for this fixVersion).
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                  {heroes.map((h) => {
                    const complete = h.total > 0 && h.done >= h.total;
                    return (
                      <Box
                        key={h.name}
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          px: 1.1,
                          py: 0.45,
                          borderRadius: `${brainTokens.radius.pill}px`,
                          background: complete ? c.successBg : c.tealTint,
                          color: complete ? c.successText : c.tealInk,
                          fontFamily: brainTokens.font.sans,
                        }}
                      >
                        {h.name}{" "}
                        <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontWeight: 500 }}>
                          {h.done}/{h.total}
                        </Box>
                        {complete ? " ✓" : ""}
                      </Box>
                    );
                  })}
                  {unlinked != null && unlinked > 0 && (
                    <Box
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        px: 1.1,
                        py: 0.45,
                        borderRadius: `${brainTokens.radius.pill}px`,
                        background: c.warnBg,
                        color: c.warnText,
                      }}
                    >
                      ⚠ {unlinked} tickets unlinked
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.75,
              p: "12px 14px",
              borderBottom: `1px solid ${c.borderLight3}`,
              flexWrap: "wrap",
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.11em",
                color: c.faint,
              }}
            >
              {epics.length ? `${shownEpics.length} OF ${epics.length} EPICS` : "EPICS"}
            </Box>
            {epics.length > 0 && (
              <Segmented
                value={epicFilter}
                onChange={setEpicFilter}
                options={[
                  { key: "all", label: "All epics" },
                  { key: "open", label: "Open only" },
                  { key: "blocked", label: "Blocked" },
                ]}
              />
            )}
          </Box>
          {epics.length === 0 ? (
            <Box sx={{ p: "18px 14px", fontSize: 13, color: c.muted }}>
              {synthesisError
                ? "Epics will appear here once the release map loads."
                : "No epics found for this fixVersion yet."}
            </Box>
          ) : shownEpics.length === 0 ? (
            <Box sx={{ p: "18px 14px", fontSize: 13, color: c.muted }}>
              No epics match this filter.
            </Box>
          ) : (
            shownEpics.map((e) => (
              <EpicRow
                key={e.epicKey}
                epic={e}
                open={openEpic === e.epicKey}
                onToggle={() => setOpenEpic((k) => (k === e.epicKey ? null : e.epicKey))}
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
